// Section 3 and 24.10: newGame(options, seed). Generates the founding settlement, computes
// capacities and housing, and assigns the founding crew (P-01). Deterministic in (options, seed).
import { z } from 'zod';
import { produce } from 'immer';
import { config, type Config } from '../config';
import { names, registryFor } from '../content';
import { SimError } from '../core/errors';
import { generateSettlement } from '../core/generate';
import { assertInvariants } from '../core/invariants';
import { roundState } from '../core/round';
import type { GameState, NewGameOptions } from '../core/types';
import { recomputeCapacity } from '../phases/shared/capacity';
import { assignHousing } from '../phases/shared/housing';
import { foundingCrew } from './assign';
import { defaultCheckInvariants, readContext } from './context';

export function newGameOptionsSchema(cfg: Config = config) {
  return z.strictObject({
    population: z.number().int().min(cfg.newGame.populationMin).max(cfg.newGame.populationMax),
    seed: z.string(),
    land: z.enum(['fertile', 'temperate', 'harsh']),
    disasterFrequency: z.enum(['calm', 'normal', 'volatile']),
    threatLevel: z.enum(['none', 'low', 'high']),
    startingStock: z.enum(['lean', 'standard', 'generous']),
    startingKnowledge: z.enum(['novice', 'mixed', 'skilled']),
    steadyTargetYears: z.union([z.literal(5), z.literal(10), z.literal(20)]),
    turnCadence: z.literal('season'),
    ironman: z.boolean(),
    observer: z.boolean(),
    preset: z.enum(['gentle', 'standard', 'unforgiving']).nullable(),
  });
}

/** The default options (Section 3) with a seed. */
export function defaultOptions(seed: string, cfg: Config = config): NewGameOptions {
  return { ...cfg.newGame.defaults, seed };
}

export interface NewGameConfig {
  cfg?: Config;
  checkInvariants?: boolean;
}

export function newGame(
  options: NewGameOptions,
  seed: string,
  opts: NewGameConfig = {},
): GameState {
  const cfg = opts.cfg ?? config;
  const parsed = newGameOptionsSchema(cfg).safeParse(options);
  if (!parsed.success)
    throw new SimError('schema', `invalid new-game options: ${z.prettifyError(parsed.error)}`);
  if (seed.trim().length === 0)
    throw new SimError(
      'schema',
      'a seed is required; the UI resolves a blank seed to a random one',
    );
  const generated = generateSettlement({ cfg, options, seed, names });
  const housed = produce(generated, (draft) => {
    const ctx = readContext(draft, cfg);
    recomputeCapacity(ctx, draft);
    const housing = assignHousing(draft, ctx.registry);
    for (const [id, building] of Object.entries(housing.assignment)) {
      const h = draft.households[id as keyof typeof draft.households];
      if (h !== undefined) h.buildingId = building;
    }
    draft.ledger.peakPopulation = { value: Object.keys(draft.people).length, turn: 0 };
  });
  // The crew is planned on the housed settlement (committed, so the pipeline reads no drafts).
  const crew = foundingCrew(readContext(housed, cfg), housed);
  const state = produce(housed, (draft) => {
    for (const [id, job] of crew) {
      const p = draft.people[id];
      if (p !== undefined) p.job = job;
    }
  });
  const rounded = roundState(state);
  if (opts.checkInvariants ?? defaultCheckInvariants()) {
    assertInvariants(rounded, { cfg, registry: registryFor(cfg), phase: 'new-game' });
  }
  return rounded;
}
